import { useRef } from 'react';
import { PartnerSystems } from '@common/apis/sorting-debt/types';
import { useOmniAds } from '@libs/utils/hooks/OmniAds';
import { useRedline } from '@libs/redline';
import useGTM from '@common/hooks/useGTM';
import { getDebtCardTagDiscountDrop } from '@common/helpers/getDebtCardTagDiscountDrop';
import { createID } from 'redline-client-sdk';

export default function useDebtsTracking() {
  const metaDebtSimulatedEventSent = useRef(false);
  const GTM = useGTM();
  const { track: trackRedline } = useRedline();
  const { omniTracker } = useOmniAds();
  const correlationIdRef = useRef<string | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const sendOfferLoadStarted = () => {
    const correlationId = createID();
    correlationIdRef.current = correlationId;
    startTimeRef.current = Date.now();
    trackRedline.renegkit.offersLoadStarted({ correlationId });
  };

  const sendOfferLoadEnded = () => {
    if (!startTimeRef.current) return;
    const elapsedTimeMs = (Date.now() - startTimeRef.current);

    trackRedline.renegkit.offersLoadEnded({ correlationId: correlationIdRef.current || '', elapsedTimeMs });

    startTimeRef.current = null;
    correlationIdRef.current = null;
  };

  const sendEventDebtLocated = (partnerSystems: PartnerSystems[]) => {
    partnerSystems?.forEach((partnerSystem) => {
      trackRedline.debts.debtLocated({ partnerSystem }, () => {
        // this is a redline debounced event. code inside this callback will be debouced together.

        GTM.trigger.debtFound({
          dividaId: partnerSystem.id,
          partner: partnerSystem.name,
        });
      });
    });
  };

  const sendEventDebtSimulated = (debts: any, isPollingComplete: boolean) => {
    const metaPayloads: any[] = [];

    debts?.forEach((debt: any) => {
      const { discountDropValue } = getDebtCardTagDiscountDrop(debt);
      trackRedline.debts.debtSimulated({
        paymentOption: debt?.paymentOptions?.[0],
        debt: {
          ...debt,
          tagDiscountDropValue: discountDropValue,
          partner: debt?.partner,
          propensityToAccept: debt?.propensityToAccept,
        },
      });

      const payloadOmniAds = {
        value: debt?.paymentOptions?.[0]?.currentDebtValue,
        transaction_id: debt?.id,
        currency: 'BRL',
      };

      metaPayloads.push(payloadOmniAds);

      omniTracker.all({
        google: {
          target: 'gALL',
          eventName: 'debts.DebtSimulated.v1',
          conversionId: 'AW-392945561/JoiPCJuQp_YaEJm_r7sB',
          payload: {
            ...payloadOmniAds,
          },
        },
      });
    });

    if (isPollingComplete && metaPayloads.length > 0 && !metaDebtSimulatedEventSent.current) {
      omniTracker.all({
        meta: {
          eventName: 'Custom',
          payload: {
            eventName: `debts.DebtSimulated.v1`,
            contents: metaPayloads,
          },
        },
      });

      metaDebtSimulatedEventSent.current = true;
    }
  };

  return { sendEventDebtLocated, sendEventDebtSimulated, sendOfferLoadStarted, sendOfferLoadEnded };
}
